import { useEffect, useState } from "react";
import { adminApi } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  Users,
  UserCheck,
  Store,
  Video,
  Mail,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  TrendingUp,
  Loader2,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, color, testid, accent }) => (
  <div
    className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow"
    data-testid={testid}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" strokeWidth={2} />
      </div>
      {accent && (
        <span className="text-xs font-medium text-green-600 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> {accent}
        </span>
      )}
    </div>
    <p className="text-3xl font-bold text-gray-900 leading-none">{value ?? 0}</p>
    <p className="text-sm text-gray-500 mt-1.5">{label}</p>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/stats");
        setStats(data);
      } catch (e) {
        setError("Failed to load dashboard stats");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 text-[#FF9933] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
        {error}
      </div>
    );
  }

  const recent7 = (stats?.recent || []).slice(0, 7);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Overview of registrations and communications for Ujjain Maha Kumbh 2028.
        </p>
      </div>

      {/* Registration Stats */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Registrations
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            icon={Users}
            label="Total"
            value={stats.total_registrations}
            color="bg-gradient-to-br from-[#FF9933] to-[#E67300]"
            testid="stat-total"
          />
          <StatCard
            icon={UserCheck}
            label="Pilgrims"
            value={stats.pilgrims}
            color="bg-gradient-to-br from-amber-500 to-amber-600"
            testid="stat-pilgrims"
          />
          <StatCard
            icon={UserCheck}
            label="Volunteers"
            value={stats.volunteers}
            color="bg-gradient-to-br from-emerald-500 to-emerald-600"
            testid="stat-volunteers"
          />
          <StatCard
            icon={Store}
            label="Vendors"
            value={stats.vendors}
            color="bg-gradient-to-br from-violet-500 to-violet-600"
            testid="stat-vendors"
          />
          <StatCard
            icon={Video}
            label="Media"
            value={stats.media}
            color="bg-gradient-to-br from-sky-500 to-sky-600"
            testid="stat-media"
          />
        </div>
      </div>

      {/* Status + Email Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Status Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Registration Status</h3>
            <Link
              to="/admin/registrations"
              className="text-xs text-[#FF9933] hover:text-[#E67300] font-medium flex items-center gap-1"
              data-testid="view-all-registrations"
            >
              Manage <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: "status_confirmed", label: "Confirmed", icon: CheckCircle2, color: "text-green-600 bg-green-50 border-green-200" },
              { key: "status_pending", label: "Pending", icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200" },
              { key: "status_approved", label: "Approved", icon: CheckCircle2, color: "text-blue-600 bg-blue-50 border-blue-200" },
              { key: "status_rejected", label: "Rejected", icon: XCircle, color: "text-red-600 bg-red-50 border-red-200" },
            ].map(({ key, label, icon: Icon, color }) => (
              <div
                key={key}
                className={`rounded-md border p-3 ${color}`}
                data-testid={`status-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4 mb-1.5" />
                <p className="text-2xl font-bold leading-none">{stats[key] ?? 0}</p>
                <p className="text-xs mt-1 opacity-80">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Email Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Email Activity</h3>
            <Link
              to="/admin/email-logs"
              className="text-xs text-[#FF9933] hover:text-[#E67300] font-medium flex items-center gap-1"
              data-testid="view-email-logs"
            >
              View <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            <EmailStat icon={Mail} label="Total" value={stats.emails_total} color="text-gray-900" />
            <EmailStat icon={CheckCircle2} label="Sent (SMTP)" value={stats.emails_sent} color="text-green-600" />
            <EmailStat icon={Clock} label="Logged (no SMTP)" value={stats.emails_logged} color="text-amber-600" />
            <EmailStat icon={XCircle} label="Failed" value={stats.emails_failed} color="text-red-600" />
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Registrations</h3>
        {recent7.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No activity yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {recent7.map((r, i) => (
              <li
                key={i}
                className="flex items-center justify-between py-2.5 text-sm"
                data-testid={`recent-activity-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      r.category === "pilgrim"
                        ? "bg-amber-500"
                        : r.category === "volunteer"
                        ? "bg-emerald-500"
                        : r.category === "vendor"
                        ? "bg-violet-500"
                        : "bg-sky-500"
                    }`}
                  />
                  <span className="capitalize text-gray-700">{r.category || "—"} registered</span>
                </div>
                <span className="text-xs text-gray-400">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EmailStat({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Icon className={`w-4 h-4 ${color}`} />
        <span>{label}</span>
      </div>
      <span className={`font-semibold ${color}`}>{value ?? 0}</span>
    </div>
  );
}
